/********************************************************************
 * @author:      Kaven
 * @email:       kaven@wuwenkai.com
 * @website:     http://blog.kaven.xyz
 * @file:        [Kaven-Basic] /src/libs/core/Countdown.ts
 * @create:      2025-07-01 13:21:24.701
 * @modify:      2025-07-11 21:19:12.934
 * @version:     6.0.0
 * @times:       13
 * @lines:       104
 * @copyright:   Copyright © 2018-2025 Kaven. All Rights Reserved.
 * @description: [description]
 * @license:     
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 * 
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 * 
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 ********************************************************************/

/**
 * Starts a countdown from the specified number, invoking an optional action at each interval.
 *
 * @param count - The starting number for the countdown. If less than or equal to 0, resolves immediately with 0.
 * @param interval - The interval in milliseconds between each countdown tick. Defaults to 1000ms (1 second). If less than or equal to 0, it is set to 1ms.
 * @param intervalAction - An optional callback invoked at each tick with the current count left. If this callback returns `false`, the countdown stops early.
 * @returns A Promise that resolves with the remaining count (usually 0) when the countdown finishes or is stopped early, or rejects if an error occurs in the intervalAction.
 * 
 * @since 1.1.5
 * @version 2025-07-10
 */
export function Countdown(count: number, interval = 1000, intervalAction?: (left: number) => boolean | void): Promise<number> {
    if (count <= 0) {
        return Promise.resolve(0);
    }

    if (interval <= 0) {
        interval = 1;
    }

    return new Promise<number>((resolve, reject) => {
        let left = count;
        let finished = false;

        const done = (error?: unknown) => {
            /* istanbul ignore next */
            if (finished) {
                return;
            }

            finished = true;
            clearInterval(id);

            if (error) {
                reject(error);
            } else {
                resolve(left);
            }
        };

        const action = () => {
            if (!intervalAction) {
                return;
            }

            try {
                if (intervalAction(left) === false) {
                    done();
                }
            } catch (ex) {
                done(ex);
            }
        };

        action();

        if (finished) {
            return;
        }

        const id = setInterval(() => {
            left--;

            if (left > 0) {
                action();
            } else {
                done();
            }
        }, interval);
    });
}
